import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarAPI } from "../../api/calendar";
import { AuthAPI } from "../../api/auth";

function formatDayHeading(dateString) {
  const d = new Date(dateString);
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatTime(dateString) {
  const d = new Date(dateString);
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [feedUrl, setFeedUrl] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const data = await CalendarAPI.listEvents();
        setEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Failed to load calendar");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

useEffect(() => {
  async function loadFeedUrl() {
    try {
      const me = await AuthAPI.me();

      if (me?.calendar_feed_token) {
        setFeedUrl(
          `${window.location.origin}/api/calendar/feed/${me.calendar_feed_token}`
        );
      } else {
        setFeedUrl("");
      }
    } catch {
      setFeedUrl("");
    }
  }

  loadFeedUrl();
}, []);

  const groupedEvents = useMemo(() => {
    const groups = {};

    for (const event of events) {
      const key = new Date(event.start_at).toISOString().slice(0, 10);

      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
    }

    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, items]) => ({
        date,
        items: items.sort(
          (a, b) => new Date(a.start_at) - new Date(b.start_at)
        ),
      }));
  }, [events]);

  if (loading) {
    return <div className="p-4">Loading calendar…</div>;
  }

  if (error) {
    return <div className="p-4 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Calendar</h1>
        <p className="text-sm text-gray-600 mt-1">
          Scheduled tickets now. Annual leave will plug into this later.
        </p>
      </div>

      <div className="border rounded bg-white p-3">
        <div className="font-medium">Calendar subscription</div>
        <div className="text-sm text-gray-600 mt-1">
            Use this link in Outlook or another calendar app to subscribe.
        </div>

        {feedUrl ? (
            <div className="mt-2 flex gap-2 items-center">
            <input
                type="text"
                readOnly
                value={feedUrl}
                className="border rounded px-2 py-1 w-full text-sm"
            />
            <button
                className="px-3 py-1 rounded bg-blue-600 text-white"
                onClick={() => navigator.clipboard.writeText(feedUrl)}
            >
                Copy
            </button>
            </div>
        ) : (
            <div className="mt-2 text-sm text-gray-500">
            No calendar feed token found for this user.
            </div>
        )}
        </div>

      {groupedEvents.length === 0 ? (
        <div className="border rounded bg-white p-4 text-sm text-gray-500">
          No calendar events found.
        </div>
      ) : (
        groupedEvents.map((group) => (
          <div key={group.date} className="space-y-3">
            <h2 className="text-lg font-semibold">
              {formatDayHeading(group.date)}
            </h2>

            <div className="space-y-2">
              {group.items.map((event) => (
                <div
                  key={`${event.type}-${event.id}`}
                  className="border rounded bg-white p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium">
                        {event.ticket_id ? (
                          <Link
                            to={`/helpdesk/tickets/${event.ticket_id}`}
                            className="text-blue-600 underline"
                          >
                            {event.title}
                          </Link>
                        ) : (
                          event.title
                        )}
                      </div>

                      <div className="text-sm text-gray-600 mt-1">
                        {formatTime(event.start_at)} → {formatTime(event.end_at)}
                      </div>

                      <div className="text-sm text-gray-600 mt-1">
                        {event.user_names?.length
                          ? `Assigned: ${event.user_names.join(", ")}`
                          : "Unassigned"}
                      </div>
                    </div>

                    <div className="shrink-0">
                      <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                        {event.type === "ticket_schedule"
                          ? "Ticket"
                          : event.type}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}